import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, SystemHealthData } from '../utils/api';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  CpuChipIcon,
  CircleStackIcon,
  ServerIcon,
} from '@heroicons/react/24/outline';

const SystemHealth: React.FC = () => {
  const { data: healthData, isLoading, error } = useQuery<SystemHealthData>({
    queryKey: ['system-health'],
    queryFn: async () => {
      const response = await adminApi.getSystemHealth();
      return response.data;
    },
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / (24 * 3600));
    const hours = Math.floor((seconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'unhealthy': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircleIcon className="h-5 w-5" />;
      case 'warning': return <ExclamationTriangleIcon className="h-5 w-5" />;
      case 'unhealthy': return <XCircleIcon className="h-5 w-5" />;
      default: return <ServerIcon className="h-5 w-5" />;
    }
  };

  const getUsageColor = (percent: number) => {
    if (percent > 90) return 'bg-red-500';
    if (percent > 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !healthData) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <span className="text-red-700">Failed to load system health data</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            System Health
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Monitor system performance and health metrics
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(healthData.status)}`}>
            {getStatusIcon(healthData.status)}
            <span className="ml-2 capitalize">{healthData.status}</span>
          </div>
        </div>
      </div>

      {/* System Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CircleStackIcon className="h-8 w-8 text-blue-500" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-900">Database</h3>
              <p className={`text-sm ${healthData.database_healthy ? 'text-green-600' : 'text-red-600'}`}>
                {healthData.database_healthy ? 'Healthy' : 'Unhealthy'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ServerIcon className="h-8 w-8 text-red-500" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-900">Redis</h3>
              <p className={`text-sm ${healthData.redis_healthy ? 'text-green-600' : 'text-red-600'}`}>
                {healthData.redis_healthy ? 'Healthy' : 'Unhealthy'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CpuChipIcon className="h-8 w-8 text-purple-500" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-900">Uptime</h3>
              <p className="text-sm text-gray-600">
                {formatUptime(healthData.uptime_seconds)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className={`h-8 w-8 ${healthData.status === 'healthy' ? 'text-green-500' : 'text-yellow-500'}`} />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-900">Overall</h3>
              <p className="text-sm text-gray-600 capitalize">
                {healthData.status}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Resource Usage */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Resource Usage</h3>
        </div>
        <div className="p-6 space-y-6">
          {/* CPU Usage */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">CPU Usage</span>
              <span className="text-sm text-gray-500">{healthData.cpu_usage_percent}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${getUsageColor(healthData.cpu_usage_percent)}`}
                style={{ width: `${healthData.cpu_usage_percent}%` }}
              ></div>
            </div>
          </div>

          {/* Memory Usage */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Memory Usage</span>
              <span className="text-sm text-gray-500">{healthData.memory_usage_percent}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${getUsageColor(healthData.memory_usage_percent)}`}
                style={{ width: `${healthData.memory_usage_percent}%` }}
              ></div>
            </div>
          </div>

          {/* Disk Usage */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Disk Usage</span>
              <span className="text-sm text-gray-500">{healthData.disk_usage_percent}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${getUsageColor(healthData.disk_usage_percent)}`}
                style={{ width: `${healthData.disk_usage_percent}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemHealth;